import { Agent } from "./agent";
import { CBHDocument } from "./document";
import { LegacyGeoLocationCoordinatesAndType } from "./util";
import {
  RateNegotiationConfigEnum,
  RateNegotiationSchema,
} from "../../../../app/rateNegotiation/interfaces";
import { FacilityNote } from "../../../../appV2/Facilities/types";
import {
  NfcTagRequestStatus,
  ShiftPeriod,
  ShiftSpecificationCategory,
  ShiftWindow,
} from "../../../../appV2/Shifts/Shift/types";

export enum GeofenceStatusType {
  entry = "entry",
  exit = "exit",
  startBreak = "start-break",
  endBreak = "end-break",
}

export interface Facility {
  /**
   * @deprecated You are probably looking for `userId` and not this.
   * This is just a MongoDB's auto-generated internal ID which we don't regard as the facility ID in our system
   */
  _id?: string;
  userId: string;
  facilityId?: string;
  name?: string;
  phone?: string;
  fullAddress?: {
    state?: string;
    region?: string;
    formatted?: string;
    metropolitanStatisticalArea?: string;
  };
  status?: string;
  tmz?: string;
  distance?: number;
  isApproxDistance?: boolean;
  description?: string;
  profileDescription?: string;
  authorizedSignatories?: AuthorizedSignatory[];
  geoLocation?: LegacyGeoLocationCoordinatesAndType;
  instantBook?: boolean | string;
  requiredDocuments?: CBHDocument[];
  rates?: {
    CNA?: number;
    LVN?: number;
    RN?: number;
    NP?: number;
    PT?: number;
    PTA?: number;
    HHA?: number;
  };
  checkInInstructions?: string;
  isInstantPayEnabled?: boolean;
  agentHasAllDocuments?: boolean;
  verification?: VerificationPreferences;
  verificationPreference?: VerificationPreferences;
  facilityNotes?: FacilityNote[];
  locationAwareness?: string;
  profileDecriptionlastManualUpdate?: Date;
  type?: string;
  shiftsCount?: number;
  filteredShiftsCount?: number;
  shifts?: Shift[];
  rating?: {
    count: number;
    value: number;
  };
  requireTimecardPhoto?: boolean;
  is100InstantPayEnabled?: boolean;
  isTestAccount?: boolean;
  nfcTag?: boolean;
  priorityAccessShiftsCount?: number;
  windows?: Partial<Record<ShiftWindow, number>>;
  requiresLunchBreak?: boolean;
  lateCancellation?: {
    period: number;
    feeHours: number;
  };
  sentHomeChargeHours?: number;
  rateNegotiation?: RateNegotiationConfigEnum;
  featureSettings?: {
    radarTimekeepingValidations: boolean;
    displayTimeSheetSummary: boolean;
    digitalSignature: boolean;
    californiaTimesheet: boolean;
    uploadTimesheetNonIP: boolean;
  };
  quizWorkersOnRules?: boolean;
  maxAllowedWorkConsecutiveHours?: number;
  preventDoubleShifts?: boolean;
  maxAllowedWorkHoursPerWeek?: number;
}

export enum VerificationMethod {
  TIMESHEET = "Timesheet Only",
  OUTBOUND = "Outbound Verification",
  SHEET = "External Google Sheet",
  OTHER = "Other",
}
type ContactMethod = "PHONE" | "EMAIL" | "TEXT";
type Day = "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
interface ContactAvailability {
  day: Day;
}
interface VerificationContact {
  archived: boolean;
  contactMethod: {
    name: ContactMethod;
  };
  availability: ContactAvailability[];
}
export interface VerificationPreferences {
  mongodbId: string;
  name: string;
  maxOutreachAttemptsPerDay: number;
  usesTimesheets: boolean;
  verificationMethod: {
    name: VerificationMethod;
  };
  contacts: VerificationContact[];
  outboundContactList?: VerificationContact[];
  preferenceType?: string;
  verificationAttempt?: number;
  requiresLunchBreak?: boolean;
}

interface AuthorizedSignatory {
  _id?: string;
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
}

export interface TimeRange {
  startCard?: string;
  endCard?: string;
  start?: string;
  end?: string;
  isSkipped?: boolean;
  startImage?: string;
  endImage?: string;
}

interface DisputedTimeRange {
  stamp: Date;
  stage: string;
}

interface TimecardFiles {
  _id: string;
  url: string;
  signedType: string;
  timesheet: string;
  fileStorageFileKey: string;
  filename: string;
  fileStorageRootFolder: string;
}

export interface Timecard {
  createdAt?: Date;
  files?: TimecardFiles[];
  status?: number;
}

export interface TimecardV2 extends Timecard {
  success: boolean;
  error: string | null;
  response: object;
}

enum ExtraWorkedTimeType {
  BREAK = "BREAK",
}

type ExtraWorkedTimeAuthorization = {
  reason: string;
};

export type ExtraWorkedTimeParameters = {
  type: ExtraWorkedTimeType;
  authorization: ExtraWorkedTimeAuthorization;
};

interface SurgerySpeciality {
  speciality: string;
  hasTrayAssemblyExperience: boolean;
  hasSedationExperience: boolean;
}

/**
 * Most of these enums were copied from backend-main/src/constants or similar project files
 * We should centralize them here in the future
 */

enum InstantPayPaymentTypes {
  INITIAL = "INITIAL",
  REMAINING = "REMAINING",
  BREAK_EXTRA_WORKED_TIME = "BREAK_EXTRA_WORKED_TIME",
}

enum InstantPayPaymentVia {
  STRIPE = "STRIPE",
  DAILY_PAY = "DAILY_PAY",
}

enum InstantPayTimeLogStates {
  ASSIGNED = "ASSIGNED",
  ASSIGNED_FAILED = "ASSIGNED_FAILED",
  CLOCK_IN = "CLOCK_IN",
  CLOCK_OUT = "CLOCK_OUT",
  LUNCH_IN = "LUNCH_IN",
  LUNCH_OUT = "LUNCH_OUT",
  ADMIN_CLOCK = "ADMIN_CLOCK",
  ADMIN_LUNCH = "ADMIN_LUNCH",
  PARTIALY_PAID = "PARTIALY_PAID",
  FULL_PAID = "FULL_PAID",
  CANCELED = "CANCELLED",
  VERIFIED = "VERIFIED",
  TIME_ADJUSTED = "TIME_ADJUSTED",
  SKIP_LUNCH = "SKIP_LUNCH",
  SHIFT_NOTE_UPDATE = "SHIFT_NOTE_UPDATE",
  SHIFT_TIME_CHANGE = "SHIFT_TIME_CHANGE",
  POST_VERIFY_CLOCK_IN = "POST_VERIFY_CLOCK_IN",
  POST_VERIFY_CLOCK_OUT = "POST_VERIFY_CLOCK_OUT",
  POST_VERIFY_LUNCH_IN = "POST_VERIFY_LUNCH_IN",
  POST_VERIFY_LUNCH_OUT = "POST_VERIFY_LUNCH_OUT",
  AUTO_VERIFY = "AUTO_VERIFY",
  BREAK_EXTRA_WORKED_TIME_PAID = "BREAK_EXTRA_WORKED_TIME_PAID",
}

interface InstantPayShiftPayment {
  amount: number;
  paidVia: InstantPayPaymentVia;
  type: InstantPayPaymentTypes;
}

export interface ShiftGeofence {
  entry: Date;
  exit?: Date;
  startBreak?: Date;
  endBreak?: Date;
  status: GeofenceStatusType;
}

interface InstantPayShiftDetails {
  shiftId: string;
  hcpId: string;
  start: string;
  end: string;
  verified: boolean;
  deleted: boolean;
  status?: InstantPayTimeLogStates;
  action: InstantPayTimeLogStates;
  payments: InstantPayShiftPayment[];
  charge: number;
  time: number;
  pay: number;
  totalAmount: number;
  paidAmount: number;
  /**
   * `remaingAmount` is a typo from the API.
   */
  remaingAmount: number;
  hcpClockIn?: string;
  hcpLunchOut?: string;
  hcpLunchIn?: string;
  hcpClockOut?: string;
  adminClockIn?: string;
  adminLunchIn?: string;
  adminLunchOut?: string;
  adminClockOut?: string;
  completedDate?: string;
  postVerifyClockIn?: string;
  postVerifyClockOut?: string;
  postVerifyLunchIn?: string;
  postVerifyLunchOut?: string;
  is100InstantPayEnabled?: boolean | undefined;
  nfcTag?: boolean;
  submitClockIn?: string;
  submitLunchIn?: string;
  submitLunchOut?: string;
  submitClockOut?: string;
}

export interface Shift {
  _id?: string;
  name?: ShiftPeriod;
  verified?: boolean;
  signed?: boolean;
  agentReq?: string;
  confirmed?: boolean;
  filtered?: boolean;
  start?: string;
  bookedAt?: string;
  end?: string;
  time?: number;
  description?: string;
  surgerySpeciality?: SurgerySpeciality;
  charge?: number;
  pay?: number;
  distance?: number;
  isApproxDistance?: boolean;
  finalPay?: number;
  offer?: {
    id: string;
    pay: {
      value: number;
      currency: string;
    };
    expiresAt?: Date;
  };
  facility?: Facility;
  facilityId?: string;
  agentId?: string;
  agent?: Agent;
  interested?: string[];
  signatory?: AuthorizedSignatory;
  unit?: string;
  clockInOut?: TimeRange;
  lunchInOut?: TimeRange;
  disputedTimeout?: DisputedTimeRange;
  agentSignature?: string;
  timecard?: Timecard;
  geofence?: ShiftGeofence;
  isHolidayShift?: boolean;
  holidayName?: string;
  rating?: {
    FACILITY?: number;
    AGENT?: number;
  };
  cancellationReason?: {
    type: string;
  };
  status?: number;
  isHCFInstantPayProhibited?: boolean;
  instantPayDetails?: InstantPayShiftDetails;
  agentHasAllDocuments?: boolean;
  isInstantPayEnabled?: boolean;
  shiftId?: string;
  isInstantBook?: boolean;
  isInstantPay?: boolean;
  isChangedToNonInstantPay?: boolean;
  instruction?: string;
  isHCFConfirmed?: boolean;
  /**
   * Only present in response for lastMinuteShifts.
   */
  isInstantForAgent?: boolean;
  isInstantPayWorkedBefore?: boolean;
  groupId?: string;
  urgency?: number;
  hidden?: number;
  bookable?: boolean;
  timecardNotAvailable?: {
    reason: string;
    setBy: string;
    updatedAt: string;
  };
  isStripePayment?: boolean;
  reminderNotify?: [];
  submitClockInOut?: TimeRange;
  submitLunchInOut?: TimeRange;
  autoClockedOut?: boolean;
  deleted?: boolean;
  isBillable?: boolean;
  nfcTag?: boolean;
  originalAmount?: number;
  priorityTill?: string;
  window?: ShiftWindow;
  nfcTagRequests?: tagRequest[];
  extendBookabilityTo?: Date;
  urgentlyBooked?: boolean;
  rateNegotiation?: RateNegotiationSchema;
  createdAt?: string;
  isExternal?: boolean;
  hasPreviousUnassign?: boolean;
  shiftTimeNegotiationActive?: boolean;
  isRateNegotiationAllowed?: boolean;
  /**
   * payBreakDown and chargeBreakDown are deprecated
   * See: https://clipboardhealth.atlassian.net/browse/CH-22416
   * Use `isChargeUpdatedManually` instead
   */
  isChargeUpdatedManually?: boolean;
  hasSentHomeRequest?: boolean;
  /**
   * Whether this is a hot shift (subsidized because it's in a GTM market)
   */
  shiftBlockId?: string;
  attendance?: {
    arrivedFacilityGeofenceAt?: string;
    leftFacilityGeofence: boolean;
    leftFacilityGeofenceAt?: string;
    isWorkerFarFromFacility: boolean;
  };
  shiftSpecifications?: {
    category: ShiftSpecificationCategory;
    requirement: string;
  }[];
  isAfterAnotherShift?: boolean;
  isSecondShiftInDouble?: boolean;
  isTransferrable?: boolean;
}
export type LegacyShift = Shift;

interface tagRequest {
  tagStatus: NfcTagRequestStatus;
  tagLocation?: string;
}

export interface Shift_NEW {
  response: [] | object | null;
  error: string | null;
  success: boolean;
  instantPayDetails?: { [key: string]: any };
  agentHasAllDocuments?: boolean;
}

export interface UnverifiedShift extends Shift {
  businessrules?: {
    key: string;
    value: string;
    description: string;
  };
}

export enum ShiftStages {
  CLOCK_IN = "CLOCK_IN",
  LUNCH_IN = "LUNCH_IN",
  LUNCH_OUT = "LUNCH_OUT",
  CLOCK_OUT = "CLOCK_OUT",
  SHIFT_TIME_DONE = "SHIFT_TIME_DONE",
  SKIP_LUNCH = "SKIP_LUNCH",
  UPLOAD_TIME_CARD = "UPLOAD_TIME_CARD",
  GET_FACILITY_SIGNATURE = "GET_FACILITY_SIGNATURE",
  GET_SIGNATURE = "GET_SIGNATURE",
  QR_CODE_SIGNATURE = "QR_CODE_SIGNATURE",
  QR_CODE_CLOCK_OUT = "QR_CODE_CLOCK_OUT",
}

export enum DeviceNFCCapabilityForShift {
  NFC_ENABLED = "NFC_ENABLED",
  NO_NFC = "NO_NFC",
  NFC_DISABLED = "NFC_DISABLED",
}
